import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDeleteOutline } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { DELETE_APP_RELEASE_NOTE, GET_APP_RELEASE_NOTES, SAVE_APP_RELEASE_NOTE } from "./graphql";

// "What's new" in the professionals' app, per app version, in English and
// Hinglish. Published notes show in the app under Profile -> What's new for
// that version and newer builds; a "New" badge shows until they open it.

const KINDS = [
  { value: "MUST_DO", label: "Must do", hint: "Shown first, open, with an orange edge" },
  { value: "NEW", label: "New", hint: "New feature" },
  { value: "IMPROVED", label: "Made better", hint: "Small improvement" },
];
const ACTION_LABELS = {
  ATTENDANCE: "Attendance",
  MY_PAY: "My pay",
  PAYOUTS: "Payouts",
  RULEBOOK: "Rules & deductions",
  LEAVES: "Leave Management",
  STOCK_REQUEST: "Ask for stock",
  INVENTORY: "My Inventory",
  WEEKLY_AUDIT: "Weekly audit",
  FEEDBACK: "Feedback dashboard",
  COMPLETED_BOOKINGS: "Completed bookings",
  CONTACT_US: "Contact us",
  REWARDS: "Rewards (hidden while rewards are off)",
};

const pair = (v) => ({ en: v?.en || "", hi: v?.hi || "" });
const blankItem = () => ({ kind: "NEW", icon: "sparkle", action: null, title: pair(), body: pair(), steps: [] });
const blankNote = () => ({ id: null, version: "", title: pair(), intro: pair(), items: [blankItem()], published: false });
const toForm = (n) => ({
  id: n.id,
  version: n.version,
  title: pair(n.title),
  intro: pair(n.intro),
  items: (n.items || []).map((it) => ({ ...it, title: pair(it.title), body: pair(it.body), steps: (it.steps || []).map(pair) })),
  published: n.published,
});

const input = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400";

// English and Hinglish side by side.
const Bilingual = ({ label, value, onChange, multiline, placeholder }) => {
  const Field = multiline ? "textarea" : "input";
  return (
    <div>
      {label && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>}
      <div className="grid md:grid-cols-2 gap-2">
        {["en", "hi"].map((lang) => (
          <label key={lang} className="block">
            <span className="text-[11px] text-gray-400">{lang === "en" ? "English" : "Hinglish (Roman letters)"}</span>
            <Field
              rows={multiline ? 3 : undefined}
              value={value[lang]}
              placeholder={lang === "en" ? placeholder : "Khaali chhodein toh English dikhega"}
              onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
              className={input}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

const ItemEditor = ({ item, index, count, icons, actions, onChange, onMove, onRemove }) => {
  const set = (patch) => onChange({ ...item, ...patch });
  const setStep = (i, v) => set({ steps: item.steps.map((s, j) => (j === i ? v : s)) });
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${item.kind === "MUST_DO" ? "border-amber-300 bg-amber-50/40" : "border-gray-200"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">Card {index + 1}</span>
        <select value={item.kind} onChange={(e) => set({ kind: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1 text-sm" title={KINDS.find((k) => k.value === item.kind)?.hint}>
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <select value={item.icon} onChange={(e) => set({ icon: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1 text-sm">
          {icons.map((i) => <option key={i} value={i}>Icon: {i}</option>)}
        </select>
        <select value={item.action || ""} onChange={(e) => set({ action: e.target.value || null })} className="border border-gray-200 rounded-lg px-2 py-1 text-sm">
          <option value="">No "Take me there" button</option>
          {actions.map((a) => <option key={a} value={a}>Button opens: {ACTION_LABELS[a] || a}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1 text-gray-500">
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Move up"><MdArrowUpward /></button>
          <button type="button" disabled={index === count - 1} onClick={() => onMove(1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Move down"><MdArrowDownward /></button>
          <button type="button" onClick={onRemove} className="p-1.5 rounded hover:bg-red-50 hover:text-red-600" aria-label="Remove card"><MdDeleteOutline /></button>
        </div>
      </div>
      <Bilingual label="Title" value={item.title} onChange={(v) => set({ title: v })} placeholder="e.g. Mark present every day" />
      <Bilingual label="Why it matters" value={item.body} onChange={(v) => set({ body: v })} multiline placeholder="One or two short sentences" />
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">How to do it (steps)</p>
        <div className="space-y-2">
          {item.steps.map((s, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="mt-2 w-6 h-6 shrink-0 rounded-full bg-gray-100 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
              <div className="flex-1"><Bilingual value={s} onChange={(v) => setStep(i, v)} placeholder="e.g. Open Profile > My pay" /></div>
              <button type="button" onClick={() => set({ steps: item.steps.filter((_, j) => j !== i) })} className="mt-6 p-1.5 rounded text-gray-400 hover:text-red-600" aria-label="Remove step"><MdDeleteOutline /></button>
            </div>
          ))}
          <button type="button" onClick={() => set({ steps: [...item.steps, pair()] })} className="text-sm text-gray-700 font-medium flex items-center gap-1 hover:text-gray-900"><MdAdd /> Add step</button>
        </div>
      </div>
    </div>
  );
};

const AppUpdates = () => {
  const { data, loading, refetch } = useQuery(GET_APP_RELEASE_NOTES, { variables: { app: "EMPLOYEE" }, fetchPolicy: "network-only" });
  const [save, { loading: saving }] = useMutation(SAVE_APP_RELEASE_NOTE);
  const [remove] = useMutation(DELETE_APP_RELEASE_NOTE);
  const res = data?.getAppReleaseNotes;
  const notes = res?.notes || [];
  const icons = res?.icons || ["sparkle"];
  const actions = res?.actions || [];
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!form && notes.length) setForm(toForm(notes[0]));
  }, [notes, form]);

  const setItems = (items) => setForm((f) => ({ ...f, items }));
  const moveItem = (i, d) => {
    const items = [...form.items];
    [items[i], items[i + d]] = [items[i + d], items[i]];
    setItems(items);
  };

  const onSave = async (published) => {
    setMsg(null);
    const out = await save({ variables: { data: { ...form, app: "EMPLOYEE", published } } });
    const r = out.data?.saveAppReleaseNote;
    if (r?.status === "SUCCESS") {
      setForm(toForm(r.note));
      setMsg({ ok: true, text: published ? `Published. Professionals on ${r.note.version} or newer see it in Profile > What's new.` : "Saved as draft. Professionals don't see it yet." });
      refetch();
    } else setMsg({ ok: false, text: r?.message || "Could not save." });
  };

  const onDelete = async () => {
    if (!form.id || !window.confirm(`Delete the notes for ${form.version}? This can't be undone.`)) return;
    const out = await remove({ variables: { id: form.id } });
    const r = out.data?.deleteAppReleaseNote;
    if (r?.status === "SUCCESS") {
      setForm(null);
      setMsg({ ok: true, text: "Deleted." });
      refetch();
    } else setMsg({ ok: false, text: r?.message || "Could not delete." });
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-6xl space-y-5 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-white">App updates</h1>
              <p className="text-sm text-gray-400 mt-0.5">
                What's new in the professionals' app, per version. Write each card in English and Hinglish - published notes show in Profile &gt; What's new.
              </p>
            </div>
            <button type="button" onClick={() => { setForm(blankNote()); setMsg(null); }} className="px-3 py-2 rounded-lg bg-[#FBB033] text-gray-900 text-sm font-semibold flex items-center gap-1">
              <MdAdd /> New version
            </button>
          </div>

          {res?.status === "ERROR" && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{res.message}</div>}
          {msg && <div className={`rounded-xl px-4 py-3 text-sm border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}>{msg.text}</div>}

          <div className="grid lg:grid-cols-[220px_1fr] gap-5 items-start">
            <aside className="bg-white rounded-xl border border-gray-100 shadow-sm p-2 space-y-1">
              {loading && <p className="p-3 text-sm text-gray-400">Loading…</p>}
              {!loading && !notes.length && <p className="p-3 text-sm text-gray-400">No notes yet.</p>}
              {notes.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => { setForm(toForm(n)); setMsg(null); }}
                  className={`w-full text-left rounded-lg px-3 py-2 ${form?.id === n.id ? "bg-gray-900 text-white" : "hover:bg-gray-50 text-gray-900"}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm">{n.version}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${n.published ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>{n.published ? "Live" : "Draft"}</span>
                  </span>
                  <span className={`block text-xs truncate ${form?.id === n.id ? "text-gray-300" : "text-gray-500"}`}>{n.title?.en}</span>
                </button>
              ))}
            </aside>

            {form ? (
              <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-5">
                <div className="flex flex-wrap items-end gap-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">App version</span>
                    <input value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} placeholder="1.0.26" className={`${input} w-32 block mt-1`} />
                  </label>
                  <p className="text-xs text-gray-500 max-w-md pb-2">
                    Same as the version in the app build. Phones on this version or newer see these notes; older phones don't.
                    {form.published && form.id && " Currently live."}
                  </p>
                </div>
                <Bilingual label="Headline" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} placeholder="e.g. Your pay, leave and stock - now all in the app" />
                <Bilingual label="Intro" value={form.intro} onChange={(v) => setForm((f) => ({ ...f, intro: v }))} multiline placeholder="What to read first" />

                <div className="space-y-3">
                  <p className="text-xs text-gray-500">Cards show grouped as Must do, New, then Made better - in the order below within each group.</p>
                  {form.items.map((it, i) => (
                    <ItemEditor
                      key={i}
                      item={it}
                      index={i}
                      count={form.items.length}
                      icons={icons}
                      actions={actions}
                      onChange={(v) => setItems(form.items.map((x, j) => (j === i ? v : x)))}
                      onMove={(d) => moveItem(i, d)}
                      onRemove={() => setItems(form.items.filter((_, j) => j !== i))}
                    />
                  ))}
                  <button type="button" onClick={() => setItems([...form.items, blankItem()])} className="w-full border border-dashed border-gray-300 rounded-xl py-3 text-sm font-medium text-gray-600 hover:text-gray-900 hover:border-gray-400 flex items-center justify-center gap-1">
                    <MdAdd /> Add card
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
                  <button type="button" disabled={saving} onClick={() => onSave(true)} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium disabled:opacity-50">
                    {saving ? "Saving…" : form.published ? "Save (stays live)" : "Publish"}
                  </button>
                  <button type="button" disabled={saving} onClick={() => onSave(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 disabled:opacity-50">
                    {form.published ? "Take back to draft" : "Save draft"}
                  </button>
                  {form.id && <button type="button" onClick={onDelete} className="ml-auto text-sm text-red-600 hover:underline">Delete</button>}
                </div>
                {form.id && notes.find((n) => n.id === form.id)?.updatedAt && (
                  <p className="text-xs text-gray-400">
                    Last saved {dayjs(notes.find((n) => n.id === form.id).updatedAt).format("D MMM, h:mm A")}
                    {notes.find((n) => n.id === form.id).updatedBy ? ` by ${notes.find((n) => n.id === form.id).updatedBy}` : ""}
                  </p>
                )}
              </section>
            ) : (
              !loading && <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center text-sm text-gray-500">Pick a version, or add a new one.</div>
            )}
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default AppUpdates;
